import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import { voiceAgentsApi } from '../api';
import type { ConnectionStatus, ToolCall, TranscriptEntry } from '../lib/convai-session';
import { ConvaiSession } from '../lib/convai-session';
import { loadPreviewSession, savePreviewSession } from '../lib/preview-session-storage';
import type { PreviewToolCallLogEntry, PreviewTranscriptEntry } from '../types';

export type { ConnectionStatus };

/**
 * Classifies a terminal preview connection failure so the UI can show an
 * actionable message. `'payment'` means the voice provider (ElevenLabs)
 * rejected the request for a billing/account reason — not a code bug, and only
 * resolvable by the account administrator (review the contracted plan).
 */
export type PreviewErrorKind = 'payment' | 'generic' | null;

export interface PreviewToolCallEntry extends PreviewToolCallLogEntry {}

export function useAgentPreview(voiceAgentId: string) {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id ?? '';
  const { showError } = useToast();
  const { t } = useTranslation();

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [toolCallLog, setToolCallLog] = useState<PreviewToolCallEntry[]>([]);
  const [isMicActive, setIsMicActive] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<PreviewErrorKind>(null);

  const sessionRef = useRef<ConvaiSession | null>(null);
  const errorToastShownRef = useRef(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<string[]>([]);
  const isPlayingRef = useRef(false);
  const ringToneRef = useRef<{ stop: () => void } | null>(null);
  const micActiveRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const transcriptRef = useRef<TranscriptEntry[]>([]);
  const toolCallLogRef = useRef<PreviewToolCallLogEntry[]>([]);

  const agentIdRef = useRef<string>(voiceAgentId);
  const orgIdRef = useRef<string>(orgId);

  useEffect(() => {
    agentIdRef.current = voiceAgentId;
  }, [voiceAgentId]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    toolCallLogRef.current = toolCallLog;
  }, [toolCallLog]);

  useEffect(() => {
    if (!sessionId) return;
    const stored = loadPreviewSession(voiceAgentId);
    if (!stored) return;
    savePreviewSession(voiceAgentId, {
      ...stored,
      transcript: transcript.map((e) => ({ speaker: e.speaker, text: e.text, isFinal: e.isFinal })),
      toolCallLog: toolCallLog.map((e) => ({
        id: e.id,
        name: e.name,
        args: e.args,
        result: e.result,
        timestamp: e.timestamp,
      })),
    });
  }, [transcript, toolCallLog, sessionId, voiceAgentId]);

  useEffect(() => {
    orgIdRef.current = orgId;
  }, [orgId]);

  useEffect(() => {
    micActiveRef.current = isMicActive;
  }, [isMicActive]);

  const startRingTone = useCallback(() => {
    if (Platform.OS !== 'web') return;
    try {
      const AudioContextClass =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioContextClass();

      // Browsers' autoplay policy can create the AudioContext in a 'suspended'
      // state until a user gesture resumes it — without this the ring tone is
      // silent even though playback appears to be scheduled (CA-2). Mirrors
      // the same guard already applied to playback in playNextChunk.
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const playBeep = () => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.frequency.value = 480;
        oscillator.type = 'sine';
        gain.gain.value = 0.08;
        oscillator.connect(gain).connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.4);
      };

      playBeep();
      const interval = setInterval(playBeep, 1500);

      ringToneRef.current = {
        stop: () => {
          clearInterval(interval);
          ctx.close().catch(() => {});
          ringToneRef.current = null;
        },
      };
    } catch {
      ringToneRef.current = null;
    }
  }, []);

  const stopRingTone = useCallback(() => {
    ringToneRef.current?.stop();
    ringToneRef.current = null;
  }, []);

  useEffect(() => {
    if (connectionStatus === 'connecting') {
      startRingTone();
    } else {
      stopRingTone();
    }
  }, [connectionStatus, startRingTone, stopRingTone]);

  const handleTranscript = useCallback((entry: TranscriptEntry) => {
    if (entry.speaker === 'agent') {
      setIsAgentSpeaking(!entry.isFinal);
    }
    setTranscript((prev) => {
      if (entry.isFinal) {
        return [...prev, entry];
      }
      const last = prev[prev.length - 1];
      if (last && !last.isFinal && last.speaker === entry.speaker) {
        return [...prev.slice(0, -1), entry];
      }
      return [...prev, entry];
    });
  }, []);

  const handleToolCall = useCallback(
    async (call: ToolCall) => {
      try {
        const response = await voiceAgentsApi.handlePreviewToolCall(
          voiceAgentId,
          orgId,
          sessionIdRef.current ?? '',
          call.name,
          call.args as Record<string, unknown>,
        );

        setToolCallLog((prev) => [
          ...prev,
          {
            id: call.id,
            name: call.name,
            args: call.args,
            result: response.result,
            timestamp: new Date().toISOString(),
          },
        ]);

        sessionRef.current?.sendToolResponse({
          id: call.id,
          name: call.name,
          response: response.result,
        });
      } catch {
        sessionRef.current?.sendToolResponse({
          id: call.id,
          name: call.name,
          response: { error: 'Tool call failed in preview' },
        });
      }
    },
    [voiceAgentId, orgId],
  );

  const playNextChunk = useCallback(function playNextChunkFn() {
    if (Platform.OS !== 'web') return;
    const chunk = audioQueueRef.current.shift();
    if (!chunk) {
      isPlayingRef.current = false;
      setIsAgentSpeaking(false);
      return;
    }

    isPlayingRef.current = true;
    setIsAgentSpeaking(true);

    try {
      const AudioContextClass =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;

      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextClass({ sampleRate: 16000 });
      }

      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }

      const binaryStr = atob(chunk);
      const bytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        bytes[i] = binaryStr.charCodeAt(i);
      }

      const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 32768;
      }

      const audioBuffer = ctx.createBuffer(1, float32.length, 16000);
      audioBuffer.getChannelData(0).set(float32);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      source.onended = () => playNextChunk();
      source.start();
    } catch (err) {
      console.error('[preview] audio playback error', err);
      playNextChunk();
    }
  }, []);

  const handleAudioChunk = useCallback(
    (base64: string) => {
      audioQueueRef.current.push(base64);
      if (!isPlayingRef.current) {
        playNextChunk();
      }
    },
    [playNextChunk],
  );

  const startMicCapture = useCallback(async () => {
    if (Platform.OS !== 'web') return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const AudioContextClass =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      // If playback lazy-created a context while the mic was off, close it
      // before overwriting the ref — otherwise it counts toward Chrome's
      // 6-AudioContext limit and is never released.
      audioContextRef.current?.close().catch(() => {});
      const ctx = new AudioContextClass({ sampleRate: 16000 });
      audioContextRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      // TODO(voice-agents): migrate to AudioWorkletNode — createScriptProcessor is
      // deprecated and emits a console warning in Chromium; will be removed in future
      // browsers. Keeping it for now to ship H5; track replacement in F1B-EL-SVC-EXTEND.
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (!micActiveRef.current) return;
        const inputData = event.inputBuffer.getChannelData(0);
        const int16 = new Int16Array(inputData.length);
        let energy = 0;
        for (let i = 0; i < inputData.length; i++) {
          const sample = inputData[i];
          int16[i] = Math.max(-32768, Math.min(32767, sample * 32767));
          energy += sample * sample;
        }
        const rms = Math.sqrt(energy / inputData.length);

        if (rms > 0.05 && isPlayingRef.current) {
          const fired = sessionRef.current?.interrupt() ?? false;
          if (fired) {
            audioQueueRef.current = [];
            isPlayingRef.current = false;
            setIsAgentSpeaking(false);
          }
          console.debug('[preview] barge-in check', {
            rms,
            wasPlaying: true,
            interruptFired: fired,
          });
        }

        const base64 = btoa(String.fromCharCode(...new Uint8Array(int16.buffer)));
        sessionRef.current?.sendAudioChunk(base64);
      };

      source.connect(processor);
      processor.connect(ctx.destination);
      micActiveRef.current = true;
      setIsMicActive(true);
    } catch {
      micActiveRef.current = false;
      setIsMicActive(false);
    }
  }, []);

  const stopMicCapture = useCallback(() => {
    processorRef.current?.disconnect();
    processorRef.current = null;
    for (const track of mediaStreamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    mediaStreamRef.current = null;
    // Chrome caps AudioContext at 6 concurrent instances per tab. Leaking the
    // context on every preview cycle would silently break audio after the 6th
    // start/end pair. Close it explicitly here.
    audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    micActiveRef.current = false;
    setIsMicActive(false);
  }, []);

  const toggleMic = useCallback(async () => {
    if (micActiveRef.current) {
      stopMicCapture();
      sessionRef.current?.interrupt();
      audioQueueRef.current = [];
    } else {
      await startMicCapture();
    }
  }, [startMicCapture, stopMicCapture]);

  const start = useCallback(
    async (
      existingSessionId?: string,
      existingSignedUrl?: string,
      existingDynamicVariables?: Record<string, string>,
      existingTranscript?: PreviewTranscriptEntry[],
    ) => {
      if (!orgId) return;

      // "Nueva sesión" reuses this same start() path while a previous session
      // can still be active (e.g. the user never hit "End preview"). Without
      // this teardown the prior WebSocket + mic capture + queued audio survive
      // the new ConvaiSession, so both play back at once (FLOSEAI-584). Close
      // the old session fully BEFORE opening the new one.
      if (sessionRef.current) {
        stopMicCapture();
        stopRingTone();
        audioQueueRef.current = [];
        isPlayingRef.current = false;
        sessionRef.current.close();
        sessionRef.current = null;
      }

      let sid: string;
      let wsUrl: string;
      let dynamicVariables: Record<string, string> | undefined;

      if (existingSessionId && existingSignedUrl) {
        // Resume path: the caller (handleResume) already fetched a fresh
        // signed URL + mockLeadContext via getPreviewConfig. Do NOT re-fetch
        // here — a second call mutates the stored session state (finished →
        // active again) and mints a second signed URL, racing with the one
        // the caller already has. The passed-in values are the single
        // source of truth.
        sid = existingSessionId;
        wsUrl = existingSignedUrl;
        dynamicVariables = existingDynamicVariables;
      } else {
        const data = await voiceAgentsApi.startPreview(voiceAgentId, orgId);
        sid = data.sessionId;
        wsUrl = data.convaiSignedUrl;
        dynamicVariables = data.mockLeadContext;

        savePreviewSession(voiceAgentId, {
          sessionId: sid,
          signedWsUrl: wsUrl,
          agentConvaiId: data.agentId,
          expiresAt: data.expiresAt,
        });
      }

      sessionIdRef.current = sid;
      setSessionId(sid);
      errorToastShownRef.current = false;
      setErrorKind(null);

      const convaiSession = new ConvaiSession({
        signedWsUrl: wsUrl,
        dynamicVariables,
        onStatusChange: setConnectionStatus,
        onTranscript: handleTranscript,
        onAudioChunk: handleAudioChunk,
        onToolCall: handleToolCall,
        onError: (err) => {
          console.error('ConvaiSession error', err);
          const message = err instanceof Error ? err.message : '';
          const kind: PreviewErrorKind = /payment|billing/i.test(message) ? 'payment' : 'generic';
          setErrorKind(kind);
          if (!errorToastShownRef.current) {
            errorToastShownRef.current = true;
            showError(
              t(
                kind === 'payment'
                  ? 'voiceAgents.previewCall.errorPayment.body'
                  : 'voiceAgents.previewCall.connectionError',
              ),
            );
          }
          setConnectionStatus('error');
        },
        refreshSignedUrl: async () => {
          const currentSid = sessionIdRef.current;
          if (!currentSid) throw new Error('No active session to refresh');
          const fresh = await voiceAgentsApi.getPreviewConfig(voiceAgentId, orgId, currentSid);
          // Keep dynamicVariables consistent with the session (they don't change)
          return fresh.convaiSignedUrl;
        },
      });

      sessionRef.current = convaiSession;
      if (existingSessionId) {
        // The DB-sourced transcript (from useLatestPreviewSession, passed by the
        // caller) is the source of truth for resume — localStorage can be stale
        // or absent (different tab, TTL window, end() no longer wiping it). Fall
        // back to localStorage only when the caller didn't supply one.
        const stored = loadPreviewSession(voiceAgentId);
        setTranscript(existingTranscript ?? stored?.transcript ?? []);
        setToolCallLog(stored?.toolCallLog ?? []);
      } else {
        setTranscript([]);
        setToolCallLog([]);
      }
      await convaiSession.connect();
      await startMicCapture();
    },
    [
      voiceAgentId,
      orgId,
      handleTranscript,
      handleAudioChunk,
      handleToolCall,
      startMicCapture,
      stopMicCapture,
      stopRingTone,
      showError,
      t,
    ],
  );

  const end = useCallback(async () => {
    stopMicCapture();
    stopRingTone();
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    sessionRef.current?.close();
    sessionRef.current = null;

    if (sessionId && orgId) {
      try {
        const transcriptPayload: PreviewTranscriptEntry[] = transcript.map((e) => ({
          speaker: e.speaker,
          text: e.text,
          isFinal: e.isFinal,
        }));

        await voiceAgentsApi.submitPreview(
          voiceAgentId,
          orgId,
          sessionId,
          transcriptPayload,
          toolCallLog,
        );
      } catch (err) {
        console.warn('[preview] submit failed', err);
        showError(t('voiceAgents.previewCall.submitError'));
      }
      // Do NOT clearPreviewSession here — ending a preview persists it as
      // 'finished' (resumable) in the DB via submitPreview above. Wiping the
      // local cache unconditionally was the FLOSEAI-483 regression: every "End
      // preview" silently destroyed resumability. "New session" (handleStart)
      // is the explicit fresh-start path; it naturally overwrites this same
      // localStorage key with the new session, so no manual clear is needed
      // here either.
      sessionIdRef.current = null;
      setSessionId(null);
    }

    setConnectionStatus('idle');
    setIsAgentSpeaking(false);
  }, [
    voiceAgentId,
    orgId,
    sessionId,
    transcript,
    toolCallLog,
    stopMicCapture,
    stopRingTone,
    showError,
    t,
  ]);

  useEffect(() => {
    return () => {
      sessionRef.current?.close();
      stopMicCapture();
      stopRingTone();

      const sid = sessionIdRef.current;
      const currentOrgId = orgIdRef.current;
      const currentAgentId = agentIdRef.current;
      if (sid && currentOrgId && transcriptRef.current.length > 0) {
        sessionIdRef.current = null;
        const transcriptPayload: PreviewTranscriptEntry[] = transcriptRef.current.map((e) => ({
          speaker: e.speaker,
          text: e.text,
          isFinal: e.isFinal,
        }));
        voiceAgentsApi
          .submitPreview(
            currentAgentId,
            currentOrgId,
            sid,
            transcriptPayload,
            toolCallLogRef.current,
          )
          .catch((err) => {
            console.warn('[preview] unmount submit failed', err);
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStoredSession = useCallback(() => loadPreviewSession(voiceAgentId), [voiceAgentId]);

  return {
    connectionStatus,
    errorKind,
    transcript,
    toolCallLog,
    isMicActive,
    isAgentSpeaking,
    sessionId,
    start,
    end,
    toggleMic,
    loadStoredSession,
  };
}
